import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, postMultipart } from "../../lib/api";
import { renderBoardToPng } from "../../lib/boardExport";
import { parseNoteContent, serializeNoteContent } from "../../lib/noteBlocks";
import { FullScreenModal } from "../FullScreenModal";

function formatBoardSnapshotHeading(date = new Date()) {
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function BoardExportToNoteDialog({
  open,
  onClose,
  clientId,
  clientName,
  elements,
  onSaved,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("existing");
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [newTitle, setNewTitle] = useState("");

  const loadNotes = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/portal/notes?client_id=${clientId}`);
      setNotes(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Could not load notes");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!open) return;
    loadNotes();
    setMode("existing");
    setSelectedNoteId("");
    setNewTitle(`Board snapshot · ${clientName || "Studio"}`);
  }, [open, loadNotes, clientName]);

  useEffect(() => {
    if (notes.length && !selectedNoteId) {
      setSelectedNoteId(notes[0].id);
    }
  }, [notes, selectedNoteId]);

  const appendBoardToNote = async (noteId, existingContent) => {
    const { blob } = await renderBoardToPng(elements);
    const heading = formatBoardSnapshotHeading();
    const fd = new FormData();
    fd.append("file", blob, `board-snapshot-${Date.now()}.png`);
    fd.append("folder", "presentations");
    fd.append("name", `Board snapshot · ${heading}`);
    fd.append("status", "draft");
    fd.append("version", "v1");
    fd.append("library_visible", "false");
    fd.append("studio_only", "true");

    const uploaded = await postMultipart(`/portal/files/upload?client_id=${clientId}`, fd);

    const merged = [
      ...parseNoteContent(existingContent),
      { type: "divider" },
      { type: "h4", text: heading },
      { type: "image", fileId: uploaded.id, alt: "Board snapshot" },
    ];

    const { data } = await api.patch(`/portal/notes/${noteId}?client_id=${clientId}`, {
      content: serializeNoteContent(merged),
    });
    return data;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!elements?.length) {
      toast.error("Nothing on the board to export");
      return;
    }

    setBusy(true);
    try {
      let note;
      if (mode === "new") {
        const title = newTitle.trim();
        if (!title) {
          toast.error("Name your note");
          setBusy(false);
          return;
        }
        const { data: created } = await api.post(`/portal/notes?client_id=${clientId}`, {
          title,
          content: "",
          category: "general",
          status: "draft",
        });
        note = await appendBoardToNote(created.id, "");
      } else {
        if (!selectedNoteId) {
          toast.error("Choose a note");
          setBusy(false);
          return;
        }
        const existing = notes.find((n) => n.id === selectedNoteId);
        note = await appendBoardToNote(selectedNoteId, existing?.content || "");
      }

      toast.success("Board added to note");
      onSaved?.(note);
      onClose();
    } catch (error) {
      toast.error(error.message || "Could not add board to note");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <FullScreenModal
      onClose={onClose}
      ariaLabel="Add board to note"
      testid="board-export-note-modal"
      contentClassName="max-h-[90vh] overflow-y-auto"
    >
      <div className="label-eyebrow mb-3">{clientName || "Client"}</div>
      <h2 className="mb-2 font-heading text-2xl tracking-tight">Add board to note</h2>
      <p className="mb-6 text-sm font-light text-[var(--t-meta)]">
        Exports the current board as an image and appends it to a meeting document.
      </p>

      <form onSubmit={submit} className="space-y-5">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="board-export-mode"
              checked={mode === "existing"}
              onChange={() => setMode("existing")}
            />
            Add to existing note
          </label>
          {mode === "existing" ? (
            <select
              className="t-input !rounded-[var(--t-event-pill-radius)] w-full bg-[#FAFBFC]"
              value={selectedNoteId}
              onChange={(e) => setSelectedNoteId(e.target.value)}
              disabled={loading || !notes.length}
            >
              {loading ? <option>Loading notes…</option> : null}
              {!loading && !notes.length ? <option>No notes yet</option> : null}
              {notes.map((note) => (
                <option key={note.id} value={note.id}>
                  {note.title || "Untitled note"}
                </option>
              ))}
            </select>
          ) : null}
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="board-export-mode"
              checked={mode === "new"}
              onChange={() => setMode("new")}
            />
            Create new note
          </label>
          {mode === "new" ? (
            <input
              className="t-input !rounded-[var(--t-event-pill-radius)] w-full bg-[#FAFBFC]"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Note title"
            />
          ) : null}
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="t-btn-secondary !rounded-[var(--t-event-pill-radius)] flex-1">
            Cancel
          </button>
          <button
            type="submit"
            className="t-btn-primary !rounded-[var(--t-event-pill-radius)] flex-1"
            disabled={busy || (mode === "existing" && !notes.length)}
          >
            {busy ? "Adding…" : "Add to note"}
          </button>
        </div>
      </form>
    </FullScreenModal>
  );
}
